import React, { useState, useEffect, useCallback, ReactNode } from "react";
import axios from "axios";
import { COUNTRY } from "../config/api";
import countryDialCodeMap from "../utils/countryDialCodeUtils";
import CountryContext, { Coords } from "./CountryContext";

interface CountryProviderProps {
    children: ReactNode;
}

export const CountryProvider: React.FC<CountryProviderProps> = ({ children }) => {
    const [country, setCountry] = useState<string>("IN");
    const [sellerAvailableCountries, setSellerAvailableCountries] = useState<string[]>([
        "IN",
    ]);
    const [agentAvailableCountries, setAgentAvailableCountries] = useState<string[]>([
        "IN",
    ]);
    const [coords, setCoords] = useState<Coords>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [dialCode, setDialCode] = useState<string>("+91");

    const isValidCoords = (coords: any): boolean => {
        // Case 1: empty array → invalid
        if (Array.isArray(coords)) {
            if (coords.length === 0) return false;
            const { lat, lng } = coords[0] || {};
            return (
                lat != null &&
                lng != null &&
                !isNaN(Number(lat)) &&
                !isNaN(Number(lng))
            );
        }

        // Case 2: object with lat/lng
        if (coords && typeof coords === "object") {
            const { lat, lng } = coords;
            return (
                lat != null &&
                lng != null &&
                !isNaN(Number(lat)) &&
                !isNaN(Number(lng))
            );
        }

        // Case 3: anything else (null, undefined, string, etc.)
        return false;
    };

    const fetchCountry = useCallback(async (countryParam: string = "", coordsParam: Coords = {}) => {
        try {
            const validCoords = isValidCoords(coordsParam);
            const fetch = (countryParam && validCoords) ? 0 : 1;

            const response = await axios.get(COUNTRY, { params: { fetch } });

            if (response?.data?.success && response?.data?.code === 200) {
                let newCountry = countryParam;
                let newCoords = coordsParam;

                if (fetch) {
                    newCountry = response.data.data.country;
                    newCoords = response.data.data.coords;
                    localStorage.setItem(
                        "tweeklaISO",
                        JSON.stringify({
                            country: newCountry,
                            coords: newCoords,
                            timestamp: new Date().getTime(),
                        })
                    );
                }
                setCountry(newCountry);
                setCoords(newCoords);
                setDialCode(countryDialCodeMap[newCountry] || "+91");

                setSellerAvailableCountries(
                    response.data.data.seller_available_countries
                );
                setAgentAvailableCountries(
                    response.data.data.agent_available_countries
                );
            }
        } catch (err) {
            console.error("Error fetching country:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const cachedData = localStorage.getItem("tweeklaISO");
        if (cachedData) {
            try {
                const { country: cachedCountry, coords: cachedCoords, timestamp } = JSON.parse(cachedData);
                const now = new Date().getTime();
                const oneDay = 7 * 24 * 60 * 60 * 1000; // milliseconds in 7 days

                if (now - timestamp < oneDay) {
                    fetchCountry(cachedCountry, cachedCoords);
                } else {
                    localStorage.removeItem("tweeklaISO");
                    fetchCountry();
                }
            } catch (e) {
                localStorage.removeItem("tweeklaISO");
                fetchCountry();
            }
        } else {
            fetchCountry();
        }
    }, [fetchCountry]);

    return (
        <CountryContext.Provider
            value={{
                country,
                coords,
                dialCode,
                sellerAvailableCountries,
                agentAvailableCountries,
                loading,
            }}
        >
            {children}
        </CountryContext.Provider>
    );
};

export default CountryProvider;
